import React, { useRef, useState } from "react";
import PageContainer from "./layout/PageContainer.js";
import { getNetlifyFunctionsBase } from "../utils/netlifyFunctions.js";

const wordCount = (s) => (String(s).trim().match(/[^\s]+/g) || []).length;

const GetFeaturedOnPodcast = () => {
  const honeypotRef = useRef(null);
  const [hostFirstName, setHostFirstName] = useState("");
  const [hostLastName, setHostLastName] = useState("");
  const [email, setEmail] = useState("");
  const [podcastTitle, setPodcastTitle] = useState("");
  const [podcastUrl, setPodcastUrl] = useState("");
  const [description, setDescription] = useState("");
  const [hostBio, setHostBio] = useState("");
  const [artUrl, setArtUrl] = useState("");
  const [socialLinks, setSocialLinks] = useState("");
  const [extraNotes, setExtraNotes] = useState("");
  const [acceptsGuests, setAcceptsGuests] = useState(false);
  const [guestInfo, setGuestInfo] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const descWords = wordCount(description);
  const hostBioWords = wordCount(hostBio);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honeypotRef.current?.value?.trim()) return;

    if (!hostFirstName.trim() || !hostLastName.trim()) {
      setStatus({ type: "error", message: "Please enter the host’s first and last name." });
      return;
    }
    if (descWords < 100) {
      setStatus({
        type: "error",
        message: `Podcast description must be at least 100 words (currently ${descWords}).`
      });
      return;
    }
    if (hostBioWords > 300) {
      setStatus({
        type: "error",
        message: `About the host must be 300 words or fewer (currently ${hostBioWords}).`
      });
      return;
    }
    if (acceptsGuests && !guestInfo.trim()) {
      setStatus({
        type: "error",
        message: "If you accept guests, describe what you’re looking for and how to apply."
      });
      return;
    }

    setSubmitting(true);
    setStatus({ type: "", message: "" });
    try {
      const res = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/submitPodcastFeature`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          honeypot: honeypotRef.current?.value || "",
          hostFirstName,
          hostLastName,
          email,
          podcastTitle,
          podcastUrl,
          description,
          hostBio,
          artUrl,
          socialLinks,
          extraNotes,
          acceptsGuests,
          guestInfo
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Something went wrong. Please try again.");
      }
      if (data.devMode) {
        setStatus({
          type: "success",
          message:
            "Submission received (development mode). Check the Netlify function logs for the email body."
        });
      } else {
        setStatus({
          type: "success",
          message:
            "Thank you. Your submission was received. We review listings regularly; you’ll only hear from us if we need more detail or when your feature goes live."
        });
      }
      setHostFirstName("");
      setHostLastName("");
      setEmail("");
      setPodcastTitle("");
      setPodcastUrl("");
      setDescription("");
      setHostBio("");
      setArtUrl("");
      setSocialLinks("");
      setExtraNotes("");
      setAcceptsGuests(false);
      setGuestInfo("");
    } catch (err) {
      setStatus({ type: "error", message: err?.message || "Submission failed." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer className="podcast-feature-page" size="wide">
      <h1 className="podcast-feature-page__title">Get featured on a podcast</h1>
      <p className="podcast-feature-page__lead">
        If you host a podcast about mental performance, decision clarity, structured thinking, cognitive reset, tech, entrepreneurship, or
        related topics, we’d like to know more. Complete the form below. There is no charge for a standard listing on
        Ahavibes media. Incomplete submissions may be discarded.
      </p>

      <div className="podcast-feature-page__content">
        <section className="podcast-feature-page__panel" aria-labelledby="podcast-before-heading">
          <h2 id="podcast-before-heading" className="podcast-feature-page__panel-title">
            Before you start
          </h2>
          <ul className="podcast-feature-page__panel-list">
            <li>
              Include a <strong>link to square podcast art</strong> (at least 500×500 px), similar in quality to artwork
              used on Apple Podcasts. This is the image we may share when we promote your show.
            </li>
            <li>
              Provide a <strong>working link to your podcast</strong> (Apple, Spotify, RSS, or official show page).
              Submissions without a podcast URL cannot be reviewed.
            </li>
            <li>
              The podcast description should be thorough (minimum <strong>100 words</strong>) so listeners and search
              tools understand your show.
            </li>
          </ul>
        </section>

        <form className="podcast-feature-form podcast-feature-page__panel" onSubmit={handleSubmit} noValidate>
        <div className="podcast-feature-form__hp" aria-hidden="true">
          <label htmlFor="podcast-honeypot">Leave blank</label>
          <input ref={honeypotRef} id="podcast-honeypot" type="text" tabIndex={-1} autoComplete="off" />
        </div>

        {status.message ? (
          <p
            className={
              status.type === "error" ? "podcast-feature-form__alert podcast-feature-form__alert--error" : "podcast-feature-form__alert podcast-feature-form__alert--ok"
            }
            role="alert"
          >
            {status.message}
          </p>
        ) : null}

        <div className="podcast-feature-form__grid">
          <div className="podcast-feature-form__field">
            <label htmlFor="host-first">Host first name *</label>
            <input
              id="host-first"
              type="text"
              autoComplete="given-name"
              value={hostFirstName}
              onChange={(ev) => setHostFirstName(ev.target.value)}
              required
            />
          </div>
          <div className="podcast-feature-form__field">
            <label htmlFor="host-last">Host last name *</label>
            <input
              id="host-last"
              type="text"
              autoComplete="family-name"
              value={hostLastName}
              onChange={(ev) => setHostLastName(ev.target.value)}
              required
            />
          </div>
        </div>

        <div className="podcast-feature-form__field">
          <label htmlFor="pf-email">Email *</label>
          <input
            id="pf-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            required
          />
        </div>

        <div className="podcast-feature-form__field">
          <label htmlFor="pf-title">Name of podcast *</label>
          <input
            id="pf-title"
            type="text"
            value={podcastTitle}
            onChange={(ev) => setPodcastTitle(ev.target.value)}
            required
          />
        </div>

        <div className="podcast-feature-form__field">
          <label htmlFor="pf-url">Link to podcast *</label>
          <input
            id="pf-url"
            type="url"
            placeholder="https://…"
            value={podcastUrl}
            onChange={(ev) => setPodcastUrl(ev.target.value)}
            required
          />
        </div>

        <div className="podcast-feature-form__field">
          <label htmlFor="pf-desc">Podcast description * (min. 100 words)</label>
          <textarea
            id="pf-desc"
            rows={10}
            value={description}
            onChange={(ev) => setDescription(ev.target.value)}
            required
          />
          <span className="podcast-feature-form__hint">{descWords} words</span>
        </div>

        <div className="podcast-feature-form__field">
          <label htmlFor="pf-bio">About the podcast host * (max. 300 words)</label>
          <textarea
            id="pf-bio"
            rows={6}
            value={hostBio}
            onChange={(ev) => setHostBio(ev.target.value)}
            required
          />
          <span className="podcast-feature-form__hint">{hostBioWords} / 300 words</span>
        </div>

        <div className="podcast-feature-form__field">
          <label htmlFor="pf-art">Link to podcast art (URL) * — square, at least 500×500 px</label>
          <input
            id="pf-art"
            type="url"
            placeholder="https://… (direct image or hosted artwork URL)"
            value={artUrl}
            onChange={(ev) => setArtUrl(ev.target.value)}
            required
          />
        </div>

        <div className="podcast-feature-form__field">
          <label htmlFor="pf-social">Social or web links to include (optional, one per line)</label>
          <textarea id="pf-social" rows={3} value={socialLinks} onChange={(ev) => setSocialLinks(ev.target.value)} />
        </div>

        <div className="podcast-feature-form__field">
          <label htmlFor="pf-extra">
            Anything else we should know? (past guests, main topics, audience you serve)
          </label>
          <textarea id="pf-extra" rows={4} value={extraNotes} onChange={(ev) => setExtraNotes(ev.target.value)} />
        </div>

        <fieldset className="podcast-feature-form__fieldset">
          <legend>Do you accept guests on your podcast?</legend>
          <label className="podcast-feature-form__radio">
            <input
              type="radio"
              name="guests"
              checked={!acceptsGuests}
              onChange={() => setAcceptsGuests(false)}
            />
            No
          </label>
          <label className="podcast-feature-form__radio">
            <input type="radio" name="guests" checked={acceptsGuests} onChange={() => setAcceptsGuests(true)} />
            Yes
          </label>
        </fieldset>

        {acceptsGuests ? (
          <div className="podcast-feature-form__field">
            <label htmlFor="pf-guest">
              Guest information * — what you look for in a guest and how to apply (email, form, site, etc.)
            </label>
            <textarea id="pf-guest" rows={4} value={guestInfo} onChange={(ev) => setGuestInfo(ev.target.value)} />
          </div>
        ) : null}

        <button type="submit" className="primary-button" disabled={submitting}>
          {submitting ? "Submitting…" : "Submit"}
        </button>
      </form>
      </div>
    </PageContainer>
  );
};

export default GetFeaturedOnPodcast;
